import { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import { Button } from '../components/ui/button';
import { Play, Pause, RotateCcw, X, Minimize2, Maximize2, Volume2, VolumeX } from 'lucide-react';

const TimerContext = createContext();

export const useTimer = () => useContext(TimerContext);

export const TimerProvider = ({ children }) => {
  const [minutes, setMinutes] = useState(5);
  const [seconds, setSeconds] = useState(0);
  const [isRunning, setIsRunning] = useState(false);
  const [initialTime, setInitialTime] = useState(5 * 60);
  const [soundEnabled, setSoundEnabled] = useState(true);
  const [isOpen, setIsOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const audioRef = useRef(null);

  const totalSeconds = minutes * 60 + seconds;
  const progress = initialTime > 0 ? ((initialTime - totalSeconds) / initialTime) * 100 : 0;

  const playAlarm = useCallback(() => {
    if (soundEnabled && audioRef.current) audioRef.current.play().catch(() => {});
  }, [soundEnabled]);

  useEffect(() => {
    let interval;
    if (isRunning && totalSeconds > 0) {
      interval = setInterval(() => {
        setSeconds(prev => {
          if (prev === 0) {
            setMinutes(m => {
              if (m === 0) { setIsRunning(false); playAlarm(); return 0; }
              return m - 1;
            });
            return prev === 0 && minutes > 0 ? 59 : prev;
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [isRunning, totalSeconds, minutes, playAlarm]);

  const startTimer = (mins, secs = 0) => {
    setMinutes(mins);
    setSeconds(secs);
    setInitialTime(mins * 60 + secs);
    setIsRunning(true);
    setIsOpen(true);
    setIsMinimized(false);
  };

  const openTimer = () => { setIsOpen(true); setIsMinimized(false); };
  const closeTimer = () => { setIsRunning(false); setIsOpen(false); setIsMinimized(false); };
  const togglePause = () => {
    if (!isRunning && totalSeconds > 0) { setInitialTime(totalSeconds); setIsRunning(true); }
    else setIsRunning(false);
  };
  const resetTimer = () => { setIsRunning(false); setMinutes(Math.floor(initialTime / 60)); setSeconds(initialTime % 60); };

  const formatTime = (m, s) => `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;

  const value = {
    minutes, seconds, isRunning, isOpen, isMinimized, totalSeconds, progress, initialTime, soundEnabled,
    startTimer, openTimer, closeTimer, togglePause, resetTimer,
    setMinutes, setSeconds, setInitialTime, setSoundEnabled, setIsOpen, setIsMinimized,
    formatTime,
  };

  return (
    <TimerContext.Provider value={value}>
      {children}
      {isOpen && <FloatingTimer />}
      <audio ref={audioRef} preload="auto">
        <source src="data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdJivrJBhNjVgodDbq2EcBj+a2teleAILxv/C/8L/wv/C/8L/wv/C/8L/wv/C/8L/wv/C/8L/wv/C/8L/wv/C/8L/wv/C/8L/wv/C/8L/wv/C/8L/wv/C/8L/wv/C/8L/wv/C/8L/wv/C/8L/wv/C/8L/wv/C/8L/wv/C/8L/wv/C/w==" type="audio/wav" />
      </audio>
    </TimerContext.Provider>
  );
};

const FloatingTimer = () => {
  const {
    minutes, seconds, isRunning, isMinimized, totalSeconds, progress, initialTime,
    soundEnabled, togglePause, resetTimer, closeTimer, formatTime,
    setIsMinimized, setSoundEnabled,
  } = useTimer();

  const circumference = 2 * Math.PI * 45;
  const strokeDashoffset = circumference - (progress / 100) * circumference;
  const isFinished = totalSeconds === 0 && !isRunning;

  if (isMinimized) {
    return (
      <button
        onClick={() => setIsMinimized(false)}
        className={`fixed bottom-24 right-4 z-[9999] flex items-center gap-2 px-3 py-2 rounded-full shadow-lg border transition-all ${
          isFinished ? 'bg-red-500 text-white border-red-600 animate-pulse' : isRunning ? 'bg-indigo-600 text-white border-indigo-700' : 'bg-white text-slate-800 border-slate-200'
        }`}
        data-testid="timer-minimized-badge"
      >
        <div className="w-5 h-5 relative">
          <svg className="w-full h-full -rotate-90">
            <circle cx="10" cy="10" r="8" fill="none" stroke="currentColor" strokeOpacity="0.3" strokeWidth="2" />
            <circle cx="10" cy="10" r="8" fill="none" stroke="currentColor" strokeWidth="2" strokeDasharray={2 * Math.PI * 8} strokeDashoffset={2 * Math.PI * 8 - (progress / 100) * 2 * Math.PI * 8} />
          </svg>
        </div>
        <span className="text-sm font-bold font-mono">{formatTime(minutes, seconds)}</span>
      </button>
    );
  }

  return (
    <div className="fixed bottom-24 right-4 z-[9999] w-72 bg-white rounded-2xl shadow-2xl border border-slate-200 overflow-hidden" data-testid="floating-timer">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-2.5 bg-slate-50 border-b border-slate-100">
        <span className="text-xs font-semibold text-slate-600">Timer</span>
        <div className="flex items-center gap-1">
          <button onClick={() => setIsMinimized(true)} className="p-1 rounded-md hover:bg-slate-200 text-slate-400" data-testid="minimize-timer-btn">
            <Minimize2 className="w-3.5 h-3.5" />
          </button>
          <button onClick={closeTimer} className="p-1 rounded-md hover:bg-slate-200 text-slate-400" data-testid="close-timer-btn">
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>

      {/* Timer display */}
      <div className="p-5 flex flex-col items-center">
        <div className="relative w-28 h-28">
          <svg className="w-full h-full -rotate-90">
            <circle cx="56" cy="56" r="45" fill="none" stroke="#e2e8f0" strokeWidth="6" />
            <circle cx="56" cy="56" r="45" fill="none" stroke={isFinished ? '#ef4444' : '#6366f1'} strokeWidth="6" strokeLinecap="round" strokeDasharray={circumference} strokeDashoffset={strokeDashoffset} className="transition-all duration-1000 ease-linear" />
          </svg>
          <div className="absolute inset-0 flex items-center justify-center">
            <span className={`text-2xl font-bold font-mono ${isFinished ? 'text-red-500' : 'text-slate-800'}`} data-testid="floating-timer-display">
              {formatTime(minutes, seconds)}
            </span>
          </div>
        </div>

        <p className="text-xs text-slate-400 mt-2">
          {isRunning ? 'Running' : isFinished ? "Time's up!" : 'Paused'}
        </p>

        {/* Controls */}
        <div className="flex items-center gap-3 mt-4">
          <Button
            onClick={togglePause}
            disabled={totalSeconds === 0 && !isRunning}
            size="sm"
            className={`w-10 h-10 rounded-full p-0 ${isRunning ? 'bg-amber-500 hover:bg-amber-600' : 'bg-indigo-600 hover:bg-indigo-700'} text-white`}
            data-testid="floating-timer-toggle"
          >
            {isRunning ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4 ml-0.5" />}
          </Button>
          <Button onClick={resetTimer} variant="outline" size="sm" className="w-10 h-10 rounded-full p-0 border-slate-200" data-testid="floating-timer-reset">
            <RotateCcw className="w-4 h-4" />
          </Button>
          <Button onClick={() => setSoundEnabled(!soundEnabled)} variant="outline" size="sm" className={`w-10 h-10 rounded-full p-0 border-slate-200 ${!soundEnabled ? 'bg-slate-100' : ''}`}>
            {soundEnabled ? <Volume2 className="w-4 h-4" /> : <VolumeX className="w-4 h-4" />}
          </Button>
        </div>
      </div>
    </div>
  );
};
